/* eslint-disable no-nested-ternary */
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';

// Components
import { Spin } from 'top-component-library';

// Hooks
import { useGooglePlacesApi } from 'hooks/use-google-places-api';

// Icons
import { LoadingOutlined } from '@ant-design/icons';
import Location from 'models/Location';
import { TODO } from 'utils/Types';
import {
    AddressInputContainer,
    CardsContainer,
    LoadingSpinnerContainer,
    LocationCard,
    MenuItem,
    SearchContainer,
} from './styles';

interface AddressSearchType {
  className: string;
  onSearch: (locationShortId: string) => void;
  customersById: TODO;
  locations: Location[];
  searchPlaceholderText: string;
}

// takes in distance input and returns it rounded to the nearest tenth
const roundDistance = (distance) => Math.round(distance * 10) / 10;

// helper function used to sort the locations by distance
const compareLocationsByDistance = (loc1, loc2) => {
  if (loc1.distanceInMiles < loc2.distanceInMiles) {
    return -1;
  }
  if (loc1.distanceInMiles > loc2.distanceInMiles) {
    return 1;
  }
  return 0;
};

const AddressSearch = ({ className, onSearch, searchPlaceholderText, customersById, locations }: AddressSearchType) => {
  const [locationsToShow, setLocationsToShow] = useState([]);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const GooglePlacesAPI = useGooglePlacesApi();

  const checkCustomersWithinRangeOfPatron = useCallback(
    async (patronAddress) => {
      setShowLoadingSpinner(true);
      const locationsWithinRangeOfPatron = await GooglePlacesAPI.getLocationsWithinRangeOfPatron(
        customersById,
        patronAddress,
        locations.slice()
      );
      setLocationsToShow(locationsWithinRangeOfPatron || []);
      setShowLoadingSpinner(false);
    },
    [GooglePlacesAPI, customersById, locations]
  );

  return (
    <SearchContainer className={className}>
      <AddressInputContainer className="address-input-container">
        <AddressSearch.SimpleAddressInput
          name="address"
          placeholder={searchPlaceholderText}
          onAddressObjectChange={async (addressObject) => {
            setAddressSelected(addressObject);
            await checkCustomersWithinRangeOfPatron(addressObject);
          }}
        />
      </AddressInputContainer>

      {showLoadingSpinner && (
        <LoadingSpinnerContainer className="address-loading-spinner">
          <Spin tip="Searching Locations" indicator={<LoadingOutlined spin />} />
        </LoadingSpinnerContainer>
      )}

      {locationsToShow.length > 0 ? (
        <CardsContainer direction="vertical" size="large">
          {locationsToShow.sort(compareLocationsByDistance).map((location) => (
            <LocationCard.Container size="small" key={location.id}>
              <LocationCard.LocationCardBody>
                <LocationCard.LocationCardBodyHeader>
                  <LocationCard.LocationName>{location.locationName}</LocationCard.LocationName>
                  <LocationCard.LocationDistance>
                    {roundDistance(location.distanceInMiles).toFixed(1)} MILES AWAY
                  </LocationCard.LocationDistance>
                </LocationCard.LocationCardBodyHeader>
                <LocationCard.LocationPhoneAddress>{location.phone_number}</LocationCard.LocationPhoneAddress>
                <LocationCard.LocationPhoneAddress>{location.address.street}</LocationCard.LocationPhoneAddress>
              </LocationCard.LocationCardBody>

              <div>
                <LocationCard.StartOrderButton type="secondary" onClick={() => onSearch(location.locationShortId)}>
                  Start Order
                </LocationCard.StartOrderButton>
              </div>
            </LocationCard.Container>
          ))}
          {!locationsToShow.length && <MenuItem last={1}>No Nearby Locations</MenuItem>}
        </CardsContainer>
      ) : showLoadingSpinner || !addressSelected ? (
        <></>
      ) : (
        <LoadingSpinnerContainer className="no-locations-found">
          <p>No Nearby Locations</p>
        </LoadingSpinnerContainer>
      )}
    </SearchContainer>
  );
};

export default observer(AddressSearch);
